<template>
  <div>
    <cl-table-wrapper
      class-name="tw-bg-primary-grey-25 tw-px-0 tw-mt-0 tw-pt-0"
    >
      <template #before>
        <div class="tw-flex tw-items-center tw-justify-between tw-pb-2">
          <div>
            <label for="search-customers" class="tw-sr-only">Search</label>
            <div class="tw-relative md:tw-max-w-xs">
              <cl-input-search
                id="search-customers"
                :name="'search-customers'"
                test-id="msp-customers-data-table-search"
                :placeholder="t('search')"
                :size="'medium'"
                :value="filterState"
                :readonly="isRequestPending"
                @update:value="debounceSearch"
              />
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <customer-delete-button-action
              v-if="selectedCustomers.length"
              :accounts-to-delete="selectedCustomers"
              :multi-delete="props.multiSelect"
            />
            <customer-add-button-action
              :selected-msp-uuid="
                selectedMsp?.uuid || selectedAccount?.uuid || ''
              "
              :loading="isRequestPending"
              @created="getOrganisationsList"
            />
            <customers-table-columns-settings
              :tracking-event-category-base-name="trackingEventCategoryBaseName"
              :disabled="isRequestPending"
              :data-test-id="`msp-customers-data-table`"
            >
              <div class="tw-p-2 tw-border-b">
                <cl-button
                  size="small"
                  class="tw-text-primary-grey-700"
                  :disabled="isRestoreColumnsButtonDisabled"
                  @click="restoreColumnsOrder"
                  :data-test-id="`msp-customers-data-table-columns-settings-default-btn`"
                >
                  {{ t("controls-default") }}
                </cl-button>
              </div>
              <customers-table-columns-settings-item
                v-slot="slotProps"
                v-model="columnOrder"
              >
                <div class="tw-flex tw-items-center tw-gap-2">
                  <div
                    v-if="
                      visibleColumnsIds.includes(slotProps.item) &&
                      visibleColumnsIds.length <= 3
                    "
                    :data-test-id="`msp-customers-data-table-columns-${slotProps.item}-disabled-last-toggle`"
                    class="tw-peer tw-relative tw-rounded-full tw-bg-primary-grey-200 after:tw-absolute after:tw-start-[2px] after:tw-top-[2px] after:tw-rounded-full after:tw-bg-white after:tw-transition-all after:tw-content-[''] after:tw-translate-x-full after:tw-border-white peer-focus:tw-outline-none tw-h-4 tw-w-7 after:tw-h-3 after:tw-w-3"
                  />
                  <cl-toggle
                    v-else
                    size="sm"
                    :value="visibleColumnsIds.includes(slotProps.item)"
                    :data-test-id="`msp-customers-data-table-column-${slotProps.item}-toggle`"
                    @update:value="
                      () => {
                        const selectedColumn = columnsToggleList.find(
                          (column) => column.id === slotProps.item,
                        );
                        if (selectedColumn) {
                          toggleColumnVisibility(selectedColumn);
                        }
                      }
                    "
                  />
                  <div>{{ getName(slotProps.item) }}</div>
                </div>
              </customers-table-columns-settings-item>
            </customers-table-columns-settings>
          </div>
        </div>
      </template>
      <template #table>
        <cl-table>
          <cl-thead>
            <cl-tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
              :table-target="'thead'"
              class="tw-flex tw-w-full"
            >
              <cl-th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :col-span="header.colSpan"
                class="tw-block"
                :class="{
                  'tw-select-none': header.column.getCanSort(),
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    header.column.getIsPinned() === 'right',
                }"
                scope="col"
                :style="{ width: `${header.getSize()}px` }"
              >
                <div class="tw-flex tw-h-7 tw-items-center tw-justify-between">
                  <button
                    v-if="header.column.getCanSort()"
                    class="tw-flex tw-items-center tw-h-7"
                    :class="{
                      'tw-text-primary-grey-300': isRequestPending,
                    }"
                    :data-test-id="`msp-customers-data-table-header-${header.id}`"
                    :disabled="isRequestPending"
                    @click="header.column.getToggleSortingHandler()?.($event)"
                  >
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                      <cl-th-sorting
                        :sort-direction="header.column.getIsSorted()"
                      />
                    </template>
                  </button>
                  <div class="tw-flex tw-items-center tw-h-7" v-else>
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                    </template>
                  </div>
                  <div
                    v-if="header.column.getCanResize()"
                    class="tw-h-6 tw-cursor-col-resize tw-text-primary-grey-100 hover:tw-text-primary-grey-300"
                    @mousedown="($event) => onResize($event, header)"
                    @click.stop
                  >
                    |
                  </div>
                </div>
              </cl-th>
            </cl-tr>
          </cl-thead>
          <cl-tbody v-if="isRequestPending">
            <cl-tr
              class="tw-border-b tw-border-primary-grey-100/80 hover:tw-bg-primary-grey-50 tw-bg-white"
            >
              <cl-td
                class="h-2 bg-slate-200 rounded tw-animate-ptw-bg-primary-grey-25tespace-nowrap tw-px-2"
              >
                <div
                  class="tw-w-30 tw-h-5 tw-animate-pulse tw-bg-primary-grey-25"
                />
              </cl-td>
            </cl-tr>
          </cl-tbody>
          <cl-tbody v-else-if="!customersList.length">
            <cl-tr zebra>
              <cl-td
                colspan="100%"
                :class-name="'tw-text-center tw-pl-0 tw-pr-0 tw-pb-0'"
              >
                <t-empty>
                  <template #image>
                    <component :is="emptyState.icon" />
                  </template>
                  <template #title>
                    <span data-test-id="msp-customers-data-table-empty-state">
                      {{ emptyState.title }}
                    </span>
                  </template>
                  <template #description>
                    {{ emptyState.description }}
                  </template>
                </t-empty>
              </cl-td>
            </cl-tr>
          </cl-tbody>
          <cl-tbody v-else data-clarity-mask="true">
            <cl-tr
              v-for="(row, index) in table.getRowModel().rows"
              :key="row.id"
              :zebra="index % 2 === 0"
              class="tw-cursor-pointer tw-flex tw-w-full"
              @click="handleRowClick(row)"
            >
              <cl-td
                v-for="(cell, cellIndex) in row.getVisibleCells()"
                :key="cell.id"
                :class="{
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    cell.column.getIsPinned() === 'right',
                }"
                :style="{ width: `${cell.column.getSize()}px` }"
                :data-test-id="`msp-customers-data-table-tr-${index}-td-${cellIndex}`"
              >
                <FlexRender
                  v-if="['select', 'actions'].includes(cell.column.id)"
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                  @click.stop
                />
                <FlexRender
                  v-else
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </cl-td>
            </cl-tr>
          </cl-tbody>
        </cl-table>
      </template>
      <template #after>
        <div class="tw-relative">
          <div v-if="isRequestPending"><cl-skeleton-row /></div>
          <div v-else class="tw-flex tw-justify-between tw-items-baseline">
            <cl-table-page-size
              :placeholder="t('rows-per-page')"
              :page-size="table.getState().pagination.pageSize"
              :page-sizes="[10, 25, 50]"
              :handle-page-size-change="
                ($e: Event) =>
                  handlePageSizeChange(
                    Number(($e.target as HTMLInputElement).value),
                  )
              "
            />
            <cl-table-pagination-state
              :start="rowsIndexPointer.startIndex"
              :end="rowsIndexPointer.endIndex"
              :total="total.total"
              :placeholder="t('of')"
            />
            <cl-table-basic-pagination
              :previous-page-text="$t('previous-pagination')"
              :next-page-text="$t('next')"
              :can-previous-page="table.getCanPreviousPage()"
              :previous-page="table.previousPage"
              :can-next-page="table.getCanNextPage()"
              :next-page="table.nextPage"
            />
          </div>
        </div>
      </template>
    </cl-table-wrapper>
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useAzureStore } from "@/store/azure.store";
import TEmpty from "@/components/TEmpty";
import { computed, h, ref, resolveComponent } from "vue";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { useRouter } from "vue-router";
import {
  useRowsIndexPointer,
  useServerSidePagination,
  useMemoDefaultParams,
  useMemoColumnVisibility,
  useColumnVisibility,
  useMemoColumnOrder,
} from "@/composables/table-helpers";
import { useDebounceFn, useWindowSize } from "@vueuse/core";
import {
  useVueTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender,
  type RowSelectionState,
  AccessorKeyColumnDef,
  Header,
  type Row,
} from "@tanstack/vue-table";
import { transformOrganisationsToCustomers } from "./utils/transformOrganisationsToCustomers";
import dayjs from "dayjs";
import { DATA_TABLE_COOKIE } from "@/constants";
import { useFlag } from "@/composables/useFlags/useFlag.ts";
import TEnabledSolutionsQuickView from "@/components/TEnabledSolutionsQuickView";
import { Customer } from "./types";
import TCustomerConnection from "@/components/TCustomerConnection/TCustomerConnection.vue";
import { ACCOUNTS } from "@/authorisation";
import CustomersDataTableActions from "./CustomersDataTableActions/CustomersDataTableActions.vue";
import CustomerAddButtonAction from "./CustomerAddButtonAction/CustomerAddButtonAction.vue";
import CustomerDeleteButtonAction from "./CustomerDeleteButtonAction/CustomerDeleteButtonAction.vue";
import { AvailableServicesInterface } from "@/components/TEnabledSolutionsQuickView/TEnabledSolutionsQuickView.vue";
import CustomersTableColumnsSettings from "@/modules/Customers/CustomersTable/CustomersTableColumnSettings/CustomersTableColumnsSettings.vue";
import CustomersTableColumnsSettingsItem from "@/modules/Customers/CustomersTable/CustomersTableColumnSettings/CustomersTableColumnsSettingsItem.vue";
import { trackEvent } from "@/utils/track-event";
import { useMemoColumnSizing } from "@/composables/table-helpers/useMemoColumnSizing.ts";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { caseInsensitiveTableSort } from "@/utils/caseInsensitiveTableSort";
import { isAuth0UserMigrationComplete } from "@/utils/auth0Migration";
import UuidTablePopup from "@/components/UuidTablePopup/UuidTablePopup.vue";
import { TIER } from "@/constants/tier";
import { navigateToMspOrganisation } from "@/utils/navigation/organisationNavigation";
import {
  redirectToConnections as redirectToConnectionsHelper,
  redirectToConnectionsV2 as redirectToConnectionsV2Helper,
} from "@/utils/navigation/redirectToConnections";
import { redirectToCustomerDetails as redirectToCustomerDetailsHelper } from "./navigation";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

interface Props {
  multiSelect?: boolean;
}

const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const {
  orginisationsList: organisationsList,
  organisationsTotal: total,
  organisationsConnections,
} = storeToRefs(organisationsStore);
const licenceStore = useLicenceStore();
const azureStore = useAzureStore();
const { t } = useI18n();
const router = useRouter();
const { setRequestStatus, isRequestPending } = useRequestStatus();

const { isFeatureAvailable: isDashboardAvailableCustomer } = useFlag(
  "DASHBOARD_OVERVIEW_CUSTOMER",
);
const { isFeatureAvailable: isCustomerTypeColumnEnabled } = useFlag(
  "CUSTOMERS_TABLE_TYPE_COLUMN",
);
const { isFeatureAvailable: isConnectionsV2Available } =
  useFlag("CONNECTIONS_V2");
const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
  "M365_AZURE_APP_MIGRATION",
);

const props = withDefaults(defineProps<Props>(), {
  multiSelect: false,
});

const trackingEventCategoryBaseName = ref<string>("MSP Customer Table");
const cookieKey = ref<string>(DATA_TABLE_COOKIE.MSP_CUSTOMERS_LIST);

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);
const customersList = computed(() =>
  transformOrganisationsToCustomers(
    organisationsList.value as unknown as Parameters<
      typeof transformOrganisationsToCustomers
    >[0],
    organisationsConnections.value,
  ),
);

const getOrganisationsList = async () => {
  setRequestStatus({ type: PENDING });

  try {
    await organisationsStore.getOrginisationsList({
      parent_uuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
      params: {
        page: paramsState.page,
        results: paramsState.results,
        sortDir: paramsState.sortDir.toLocaleUpperCase() || "ASC",
        sortKey: paramsState.sortKey || "name",
        filter: paramsState.filter || "",
      },
      with_connections: true,
    });
  } finally {
    setRequestStatus({ type: READY });
  }
};
const setCustomer = async (payload: { id: string; name: string }) => {
  try {
    await authStore.setCustomer({
      uuid: payload.id,
      name: payload.name,
    });
  } catch {
    // Handled in axios interceptor
  }
};

const clearAzureCache = async () => {
  try {
    await azureStore.clearAzureCache();
  } catch {
    // Handled in axios interceptor
  }
};
const clearLicenceCache = async () => {
  try {
    await licenceStore.clearLicenceCache();
  } catch {
    // Handled in axios interceptor
  }
};

const { columnVisibility } = useMemoColumnVisibility(
  `${cookieKey.value}-columns`,
  {
    uuid: false,
    organisation_type: false,
  },
);

const { width } = useWindowSize();

const defaultColumnSize = () => {
  const totalFixedWidth = 40 + 80 + 570;
  const variableColumnsCount = 7 + (isCustomerTypeColumnEnabled.value ? 1 : 0);
  return (width.value - totalFixedWidth) / variableColumnsCount;
};

const { columnSizing } = useMemoColumnSizing(
  `${cookieKey.value}-column-sizing`,
  {
    name: defaultColumnSize(),
    description: 300,
    organisation_type: defaultColumnSize(),
    seats_used: defaultColumnSize(),
    seats_purchased: defaultColumnSize(),
    created_at: defaultColumnSize(),
    solutions: defaultColumnSize(),
    hasConnection: defaultColumnSize(),
  },
);

const columns: AccessorKeyColumnDef<Customer>[] = [
  {
    id: "select",
    accessorKey: "select",
    enableSorting: false,
    header: ({ table }) =>
      h(resolveComponent("cl-input-checkbox"), {
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        disabled: !props.multiSelect,
      }),
    // v2 org = managed in Auth0; render no checkbox so the row cannot be
    // selected and therefore cannot be bulk-deleted.
    cell: ({ row }) =>
      isAuth0UserMigrationComplete(row.original.auth0_migration_version)
        ? ""
        : h(resolveComponent("cl-input-checkbox"), {
            checked: row.getIsSelected(),
            indeterminate: !row.getCanSelect(),
            disabled:
              !row.getCanSelect() ||
              row.original.organisation_type === TIER.distributor,
            onChange: row.getToggleSelectedHandler(),
            "data-test-id": `msp-customers-data-table-checkbox-${row.index}`,
          }),
    enableHiding: false,
    enableResizing: false,
    size: 40,
    maxSize: 40,
  },
  {
    header: t("customer"),
    accessorKey: "name",
    enableResizing: true,
    size: columnSizing.value.name,
    cell: ({ getValue }) => {
      return h("span", {
        class:
          "tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-block",
        textContent: getValue(),
      });
    },
    sortingFn: caseInsensitiveTableSort,
  },
  {
    header: t("uuid-title"),
    accessorKey: "uuid",
    enableSorting: false,
    meta: {
      headerTooltip: t("uuid-tooltip"),
    },
    cell: ({ getValue }) => {
      const uuid = getValue<string>();

      return h(UuidTablePopup, {
        uuid: uuid,
      });
    },
  },
  {
    header: t("description-placeholder"),
    accessorKey: "description",
    enableSorting: false,
    enableResizing: true,
    size: columnSizing.value.description,
    cell: ({ getValue }) => {
      return h("span", {
        class:
          "tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-block",
        textContent: getValue(),
      });
    },
  },
  ...(isCustomerTypeColumnEnabled.value
    ? [
        {
          header: t("label-type"),
          accessorKey: "organisation_type",
          enableSorting: false,
          enableResizing: true,
          size: columnSizing.value.organisation_type,
          cell: ({ getValue }) =>
            h("span", {
              class:
                "tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-block",
              textContent: getValue(),
            }),
        } as AccessorKeyColumnDef<Customer>,
      ]
    : []),
  {
    header: t("active-users"),
    accessorKey: "seats_used",
    enableResizing: true,
    size: columnSizing.value.seats_used,
    enableSorting: false,
  },
  {
    header: t("licenses-issued"),
    accessorKey: "seats_purchased",
    enableResizing: true,
    size: columnSizing.value.seats_purchased,
    minSize: 175,
    enableSorting: false,
  },
  {
    header: t("date-created"),
    accessorKey: "created_at",
    cell: ({ getValue }) => dayjs(getValue<Date>()).format("MMM D, YYYY"),
    enableResizing: true,
    size: columnSizing.value.created_at,
  },
  {
    header: t("solutions"),
    accessorKey: "solutions",
    enableSorting: false,
    cell: ({ getValue }) => {
      return h(TEnabledSolutionsQuickView, {
        services: getValue<AvailableServicesInterface[]>(),
        dataTestId: "msp-customers-data-table",
      });
    },
    enableResizing: true,
    size: columnSizing.value.solutions,
  },
  {
    header: t("connection"),
    accessorKey: "hasConnection",
    enableSorting: false,
    cell: ({ row, getValue }) => {
      const rowIndex = Number(row.index);
      const {
        uuid,
        name,
        isLegacy,
        organisation_type: organisationType,
      } = customersList.value[rowIndex];

      if (organisationType !== TIER.customer) {
        return "";
      }

      // The legacy-app migration affordance (the "Update permissions" cell and
      // its v2 redirect) is gated behind M365_AZURE_APP_MIGRATION; with the flag
      // off the cell behaves exactly as before P-287.
      const isLegacyMigration = isAppMigrationEnabled.value && !!isLegacy;

      return h(TCustomerConnection, {
        hasConnection: getValue<boolean>(),
        isLegacy: isLegacyMigration,
        authorisationResource: ACCOUNTS,
        onClicked: () =>
          isLegacyMigration
            ? redirectToConnectionsV2({ id: uuid, name })
            : redirectToConnections({ id: uuid, name }),
      });
    },
    enableResizing: true,
    size: columnSizing.value.hasConnection,
  },
  {
    header: t("actions"),
    accessorKey: "actions",
    enableSorting: false,
    cell: ({ row }) => {
      const rowIndex = Number(row.index);
      const customer = customersList.value[rowIndex];

      const { uuid } = customer;
      return h(CustomersDataTableActions, {
        dataTestId: "msp-customers-data-table",
        customerUuid: uuid,
        mspUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid || "",
        rowIndex: rowIndex,
        organisationType: customer.organisation_type,
        auth0MigrationVersion: customer.auth0_migration_version,
        viewUrl: viewUrlForCustomer(customer),
        onUpdated: () => getOrganisationsList(),
        onClicked: () => customer && handleOrganisationNavigation(customer),
        disabled: customer.organisation_type === TIER.distributor,
      });
    },
    enableResizing: false,
    maxSize: 80,
    minSize: 80,
    size: 80,
  },
];

const { defaultParams, handleParamsChange } = useMemoDefaultParams(
  cookieKey.value,
  {
    pageSize: 10,
    sortDir: "asc",
    sortKey: "name",
    filter: "",
  },
);
const selectedRows = ref<RowSelectionState>({});
const selectedCustomers = computed(() => {
  const list = [];

  for (const id of Object.keys(selectedRows.value)) {
    const customer = customersList.value.find(
      (customer) => customer.uuid === id,
    );

    if (customer) {
      list.push(customer);
    }
  }

  return list;
});

const {
  paramsState,
  filterState,
  paginationState,
  sortingState,
  handlePageSizeChange,
  resetPageIndex,
} = useServerSidePagination({
  pageIndex: 0,
  pageSize: defaultParams.value.pageSize,
  sortDir: defaultParams.value.sortDir,
  sortKey: defaultParams.value.sortKey,
  filter: "",
});

const emptyState = computed(() =>
  paramsState.filter
    ? {
        icon: "cl-icon-empty-folder",
        title: t("no-search-results-default"),
        description: t("subtext-no-search-results-default"),
      }
    : {
        icon: "cl-icon-empty-box",
        title: t("no-search-results-default"),
        description: t("no-customers"),
      },
);

const redirectToConnections = (row: { id: string; name: string }) =>
  redirectToConnectionsHelper(row, {
    router,
    setCustomer,
    isConnectionsV2Available: isConnectionsV2Available.value,
  });

const redirectToConnectionsV2 = (row: { id: string; name: string }) =>
  redirectToConnectionsV2Helper(row, { router, setCustomer });

const redirectToCustomerDetails = (row: { id: string; name: string }) =>
  redirectToCustomerDetailsHelper(row, {
    router,
    setCustomer,
    clearAzureCache,
    clearLicenceCache,
    isDashboardAvailableCustomer: isDashboardAvailableCustomer.value,
  });

const handleOrganisationNavigation = async (customer: Customer) => {
  if (customer.organisation_type === TIER.msps) {
    await navigateToMspOrganisation(
      { router },
      { id: customer.uuid, name: customer.name },
    );
    return;
  }

  redirectToCustomerDetails({ id: customer.uuid, name: customer.name });
};

const viewUrlForCustomer = (customer: Customer) => {
  if (customer.organisation_type === TIER.msps) {
    return buildOrgUrl("msp", customer.uuid, "dashboard");
  }
  const suffix = isDashboardAvailableCustomer.value ? "dashboard" : "";
  return buildOrgUrl("customer", customer.uuid, suffix);
};

const handleRowClick = async (row: Row<Customer>) => {
  const customer = row.original;

  if (!customer) {
    return;
  }

  await handleOrganisationNavigation(customer);
};

watch(
  () => selectedMsp.value?.uuid,
  (newUuid, oldUuid) => {
    if (newUuid === oldUuid) {
      return;
    }

    resetPageIndex();
    getOrganisationsList();
  },
);

const getName = (name: string) =>
  ({
    name: t("customer"),
    uuid: t("uuid-title"),
    description: t("description-placeholder"),
    organisation_type: t("label-type"),
    seats_used: t("active-users"),
    seats_purchased: t("licenses-issued"),
    created_at: t("date-created"),
    solutions: t("solutions"),
    hasConnection: t("connection"),
    actions: t("actions"),
  })[name];
const visibleColumnsIds = computed(() =>
  table.getVisibleLeafColumns().map((column) => column.id),
);
const defaultColumnOrder = columns
  .map((column) => column.accessorKey)
  .filter((column) => !["select", "actions"].includes(column));

const { columnOrder } = useMemoColumnOrder(
  `${cookieKey.value}-column-order`,
  defaultColumnOrder,
);
const restoreColumnsOrder = () => {
  columnOrder.value = defaultColumnOrder;
  trackEvent({
    category: "MSP customers table columns",
    action: "Restore button clicked",
    name: "",
  });
};
const isRestoreColumnsButtonDisabled = computed(
  () =>
    JSON.stringify(defaultColumnOrder) === JSON.stringify(columnOrder.value),
);

const table = useVueTable({
  get data() {
    return customersList.value;
  },
  get pageCount() {
    return Math.ceil(total.value.total / defaultParams.value.pageSize);
  },
  initialState: {
    columnPinning: {
      right: ["actions"],
      left: ["select"],
    },
  },
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getSortedRowModel: getSortedRowModel(),
  onPaginationChange: (updater) => {
    updater instanceof Function
      ? (paginationState.value = updater(paginationState.value))
      : (paginationState.value = updater);
  },
  onSortingChange: (updater) => {
    updater instanceof Function
      ? (sortingState.value = updater(sortingState.value))
      : (sortingState.value = updater);
  },
  getRowId: (row) => row.uuid,
  onRowSelectionChange: (updater) => {
    updater instanceof Function
      ? (selectedRows.value = updater(selectedRows.value))
      : (selectedRows.value = updater);
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
    get sorting() {
      return sortingState.value;
    },
    get rowSelection() {
      return selectedRows.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
    get columnOrder() {
      return columnOrder.value;
    },
  },
  manualPagination: true,
  // v2 orgs are excluded from selection entirely, so "select all" skips them too.
  enableRowSelection: (row) =>
    !isAuth0UserMigrationComplete(row.original.auth0_migration_version),
  enableMultiRowSelection: props.multiSelect,
  columnResizeMode: "onChange",
  defaultColumn: {
    minSize: 155,
    size: defaultColumnSize(),
    maxSize: 500,
  },
  onColumnOrderChange: (newOrder) => newOrder,
});

const debounceSearch = useDebounceFn((phrase: string) => {
  filterState.value = phrase;
}, 1000);
const rowsIndexPointer = useRowsIndexPointer(table);

watch(
  paramsState,
  () => {
    getOrganisationsList();

    handleParamsChange({
      pageSize: paramsState.results,
      sortDir: paramsState.sortDir,
      sortKey: paramsState.sortKey,
      filter: paramsState.filter,
    });
  },
  { immediate: true, deep: true },
);

const onResize = (event: MouseEvent, header: Header<Customer, unknown>) => {
  const mouseUpHandler = () => {
    columnSizing.value = {
      ...columnSizing.value,
      [header.id]: header.getSize(),
    };
    document.removeEventListener("mouseup", mouseUpHandler);
  };

  document.addEventListener("mouseup", mouseUpHandler);

  header.getResizeHandler()?.(event);

  trackEvent({
    category: `${trackingEventCategoryBaseName.value} columns`,
    action: "Column size changed",
    name: JSON.stringify(columnSizing.value),
  });
};

watch(
  () => columnOrder.value,
  () =>
    trackEvent({
      category: `${trackingEventCategoryBaseName.value} columns`,
      action: "Column order changed",
      name: columnOrder.value.toString(),
    }),
);
watch(
  () => columnVisibility.value,
  () =>
    trackEvent({
      category: `${trackingEventCategoryBaseName.value} columns`,
      action: "Column visibility changed",
      name: JSON.stringify(columnVisibility.value),
    }),
);

const { columnsToggleList, toggleColumnVisibility } = useColumnVisibility(
  table,
  columnVisibility,
);
</script>
